/**
 * OASIS Demographics — voluntary self-identification (equal-opportunity).
 *
 * Mirrors the federal self-ID standards: OMB race/ethnicity categories and the
 * SF-256 disability / serious-health-condition list.
 *
 * PRIVACY / ACCESS (important): this block is intentionally modeled as its own
 * separable slice of the application. It is collected ONLY for aggregate
 * equal-opportunity reporting by OARM and must NOT be returned to hiring
 * officials, reviewers, or any non-OARM requester. Keeping it isolated (rather
 * than co-mingled with review-visible fields) lets the backend store and
 * access-control it independently — consistent with the "optional blocks that
 * are not returned to reviewers" design. Every question is voluntary.
 *
 * Response behavior (no-response / opted-out / partial) is DERIVABLE from these
 * fields and is computed on the reporting/admin side; nothing extra is stored
 * here for it.
 */

/** Sex — single-select. `prefer_not_to_answer` is an explicit choice, not blank. */
export type DemographicSex = 'male' | 'female' | 'prefer_not_to_answer';

/** Ethnicity — single-select (OMB). `prefer_not_to_answer` is explicit. */
export type DemographicEthnicity = 'not_hispanic_or_latino' | 'hispanic_or_latino' | 'prefer_not_to_answer';

/** Race — OMB categories, multi-select (check all that apply). */
export type DemographicRace =
	'american_indian_or_alaska_native' | 'asian' | 'black_or_african_american' | 'native_hawaiian_or_other_pacific_islander' | 'white';

/**
 * SF-256 disability / serious-health-condition codes (multi-select).
 *
 * All specific conditions below are federally "targeted" disabilities EXCEPT
 * `other_disability_or_condition`. That targeted/non-targeted classification is
 * carried alongside the display options (see demographics.options.ts) so data
 * capture stays coded and the aggregate side can classify without free text.
 */
export type DisabilityCondition =
	| 'deaf_or_hard_of_hearing'
	| 'blind_or_low_vision'
	| 'missing_extremities'
	| 'paralysis'
	| 'significant_disfigurement'
	| 'significant_mobility_impairment'
	| 'significant_psychiatric_disorder'
	| 'intellectual_disability'
	| 'developmental_disability'
	| 'traumatic_brain_injury'
	| 'dwarfism'
	| 'epilepsy_or_seizure_disorder'
	| 'other_disability_or_condition';

export interface IDemographics {
	/**
	 * Global opt-out. True when the applicant chose "I do not want to answer the
	 * demographic questions"; the UI collapses/clears every other field while it
	 * is set. Distinguishes a deliberate full opt-out from simply leaving the
	 * questionnaire blank.
	 */
	declinedAll: boolean;

	/** Single-select sex; omitted when unanswered (and not globally declined). */
	sex?: DemographicSex;

	/** Single-select ethnicity; omitted when unanswered. */
	ethnicity?: DemographicEthnicity;

	/** Selected race categories. Empty when unanswered or declined. */
	races: DemographicRace[];

	/** Mutually exclusive with `races`: prefers not to answer the race question. */
	racePreferNotToAnswer: boolean;

	/** Selected disability / serious-health conditions. */
	disabilityConditions: DisabilityCondition[];

	/** Mutually exclusive: none of the listed conditions apply. */
	disabilityNoneApply: boolean;

	/** Mutually exclusive: does not wish to answer the disability questions. */
	disabilityPreferNotToAnswer: boolean;
}
